import { LoaderCircle, Search, X } from 'lucide-react'
import React from 'react'
import { cn } from './cn'

export interface SearchInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  onClear?: () => void
  loading?: boolean
}

export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { onClear, loading = false, className, value, defaultValue, onChange, ...rest },
  forwardedRef
) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [uncontrolledValue, setUncontrolledValue] = React.useState(() => String(defaultValue ?? ''))
  const controlled = value !== undefined
  const currentValue = controlled ? String(value ?? '') : uncontrolledValue

  React.useImperativeHandle(forwardedRef, () => inputRef.current as HTMLInputElement)

  const clear = () => {
    if (onClear) {
      onClear()
    } else {
      setUncontrolledValue('')
      const input = inputRef.current
      const valueSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set
      if (input && valueSetter) {
        valueSetter.call(input, '')
        input.dispatchEvent(new Event('input', { bubbles: true }))
      }
    }
    inputRef.current?.focus()
  }

  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" aria-hidden="true" />
      <input
        ref={inputRef}
        type="search"
        value={currentValue}
        className={cn(
          'w-full h-[var(--ctl-h)] rounded-[var(--radius)] border border-border bg-background py-0 pl-9 pr-9 text-base text-foreground [font:inherit] focus:border-foreground focus:outline-2 focus:outline-[var(--fg-soft)] [&::-webkit-search-cancel-button]:appearance-none',
          className
        )}
        onChange={(event) => {
          if (!controlled) setUncontrolledValue(event.target.value)
          onChange?.(event)
        }}
        {...rest}
      />
      {currentValue !== '' ? (
        loading ? (
          <LoaderCircle className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 animate-spin text-muted" aria-label="搜尋中" />
        ) : (
          <button
            type="button"
            className="absolute right-2 top-1/2 grid size-6 -translate-y-1/2 place-items-center rounded-[var(--radius)] text-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-[var(--fg-soft)]"
            aria-label="清除搜尋"
            onClick={clear}
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        )
      ) : null}
    </div>
  )
})
